import { useMachine } from '@xstate/react';
import { useEffect, useMemo } from 'react';
import { QrCodeTemplate } from '../../../components/Templates/QrCodeTemplate';
import { AppCodeTemplateScreen } from '../../../components/Templates/AppCodeTemplateScreen';
import { CardReaderCodeTemplate } from '../../../components/Templates/CardReaderCodeTemplate';
import { DecoupledTemplateScreen } from '../../../components/Templates/DeoupledTemplate/DecoupledTemplateScreen';
import { SingleSelectableOptionSupplementalScreen } from '../../../components/screens/AwaitingCredentialsScreen/organisms/PromptSupplementalInformation/molecules/SingleSelectableOptionSupplemental/SingleSelectableOptionSupplementalScreen';
import { InstructionsSupplementalScreen } from '../../../components/screens/AwaitingCredentialsScreen/organisms/PromptSupplementalInformation/molecules/InstructionsSupplemental/InstructionsSupplementalScreen';
import { MultipleFieldsSupplementalScreen } from '../../../components/screens/AwaitingCredentialsScreen/organisms/PromptSupplementalInformation/molecules/MultipleFieldsSupplemental/MultipleFieldsSupplementalScreen';
import { SmsCodeTemplate } from '../../../components/Templates/SmsCodeTemplate/SmsCodeTemplate';
import { ChipTanTemplateScreen } from '../../../components/Templates/ChipTanTemplateScreen';
import { getTemplateField } from '../../../components/Templates/templates';
import { UnknownTemplateScreen } from '../../../components/screens/AwaitingCredentialsScreen/organisms/PromptSupplementalInformation/molecules/UnknownTemplateScreen/UnknownTemplateScreen';
import { CredentialsEntity } from '../../../models/credentials';
import { LoadingOverlay } from '../../../core/screens/LoadingOverlay/LoadingOverlay';
import { SubmitProviderInputsPayload } from '../../../components/screens/SubmitCredentials/molecules/ProviderInputs/ProviderInputs';

import { ProviderEntity } from '../../../models/provider';
import { createSupplementalInformationMachine } from '../../machines/supplementalInformationMachine';
import { TemplateViewContainer } from '../../../income-check-v2/components/TemplateViewContainer';
import { CheckboxSupplementalScreen } from '../../../components/screens/AwaitingCredentialsScreen/organisms/PromptSupplementalInformation/molecules/CheckboxSupplemental/CheckboxSupplementalScreen';
import { OtpMethodsTemplateScreen } from '../../../components/Templates/OtpMethodsTemplate/OtpMethodsTemplateScreen';
import { VerificationTemplateScreen } from '../../../components/Templates/VerificationTemplate/VerificationTemplateScreen';
import { SelectableImageView } from '../../../components/screens/AwaitingCredentialsScreen/organisms/PromptSupplementalInformation/molecules/SelectableImageSupplemental/SelectableImageView';
import { ImageCodeView } from '../../../components/screens/AwaitingCredentialsScreen/organisms/PromptSupplementalInformation/molecules/ImageCodeSupplemental/ImageCodeView';

export type SupplementalInformationMachineProps = {
  credentials: CredentialsEntity;
  provider: ProviderEntity;
  onDone: (credentials: CredentialsEntity) => void;
  onError: (credentials: CredentialsEntity) => void;
  initialMachineState?: string;
};

export const SupplementalInformationMachineView = ({
  credentials,
  provider,
  onDone,
  onError,
  initialMachineState,
}: SupplementalInformationMachineProps) => {
  const supplementalInformationMachine = useMemo(
    () =>
      createSupplementalInformationMachine({
        initialState: initialMachineState,
        initialContext: {
          credentials,
          selectedProvider: provider,
        },
      }),
    [credentials, provider, initialMachineState]
  );
  const [current, send] = useMachine(supplementalInformationMachine);
  const { supplementalInformation, legacyFields } = current.context;

  const onDecoupledSubmitHandler = (
    inputFields: SubmitProviderInputsPayload,
    isAutoSubmit?: boolean
  ) => {
    if (isAutoSubmit) {
      send('EVENT_AUTO_SUPPLEMENT_CREDENTIALS', { inputFields });
    } else {
      send({ type: 'EVENT_SUPPLEMENT_CREDENTIALS', inputFields });
    }
  };

  useEffect(() => {
    if (current.matches('uiDone')) {
      onDone(current.context.credentials);
    }

    if (current.matches('uiError')) {
      onError(current.context.credentials);

      if (current.context.error) throw current.context.error;
    }
  }, [current, onDone, onError]);

  const handleGoBack = () => {
    send('EVENT_GO_BACK');
  };

  if (
    current.matches('decisionTemplateNode') ||
    current.matches('processCredentialsPoller') ||
    current.matches('processSubmitData') ||
    current.matches('processResetSupplementalInformation') ||
    current.matches('uiError') ||
    current.matches('uiDone')
  )
    return (
      <TemplateViewContainer onGoBack={handleGoBack}>
        <LoadingOverlay />
      </TemplateViewContainer>
    );

  if (current.matches('uiPollingAppCodeTemplate'))
    return (
      <TemplateViewContainer onGoBack={handleGoBack}>
        <AppCodeTemplateScreen
          fields={supplementalInformation}
          onSubmit={(inputFields) => {
            send({ type: 'EVENT_SUPPLEMENT_CREDENTIALS', inputFields });
          }}
        />
      </TemplateViewContainer>
    );

  if (current.matches('uiPollingCardReaderTemplate'))
    return (
      <TemplateViewContainer onGoBack={handleGoBack}>
        <CardReaderCodeTemplate
          fields={supplementalInformation}
          onSubmit={(inputFields) => {
            send({ type: 'EVENT_SUPPLEMENT_CREDENTIALS', inputFields });
          }}
        />
      </TemplateViewContainer>
    );

  if (current.matches('uiPollingChipTanTemplate'))
    return (
      <TemplateViewContainer onGoBack={handleGoBack}>
        <ChipTanTemplateScreen
          fields={supplementalInformation}
          onSubmit={(inputFields) => {
            send({ type: 'EVENT_SUPPLEMENT_CREDENTIALS', inputFields });
          }}
        />
      </TemplateViewContainer>
    );

  if (current.matches('uiPollingSmsCodeTemplate'))
    return (
      <TemplateViewContainer onGoBack={handleGoBack}>
        <SmsCodeTemplate
          fields={supplementalInformation}
          onSubmit={(inputFields) => {
            send({ type: 'EVENT_SUPPLEMENT_CREDENTIALS', inputFields });
          }}
        />
      </TemplateViewContainer>
    );

  if (
    current.matches('uiPollingDecoupledTemplate') ||
    current.matches('processSubmitDecoupledData') ||
    current.matches('processDecoupledCredentialsPoller')
  )
    return (
      <TemplateViewContainer onGoBack={handleGoBack}>
        <DecoupledTemplateScreen
          elements={supplementalInformation}
          onSubmit={onDecoupledSubmitHandler}
        />
      </TemplateViewContainer>
    );

  if (
    current.matches('uiPollingQrCodeTemplate') ||
    current.matches('processSubmitQrCodeData') ||
    current.matches('processQrCodeCredentialsPoller')
  )
    return (
      <TemplateViewContainer onGoBack={handleGoBack}>
        <QrCodeTemplate fields={supplementalInformation} onSubmit={onDecoupledSubmitHandler} />
      </TemplateViewContainer>
    );

  if (current.matches('uiPollingSingleSelectTemplate'))
    return (
      <TemplateViewContainer onGoBack={handleGoBack}>
        <SingleSelectableOptionSupplementalScreen
          field={legacyFields[0]}
          handleSubmit={(inputFields) => {
            send({ type: 'EVENT_SUPPLEMENT_CREDENTIALS', inputFields });
          }}
        />
      </TemplateViewContainer>
    );

  if (current.matches('uiPollingMultiSelectImageTemplate'))
    return (
      <TemplateViewContainer onGoBack={handleGoBack}>
        <SelectableImageView
          allowMultipleSelection
          fields={supplementalInformation}
          onSubmit={(inputFields) => {
            send({ type: 'EVENT_SUPPLEMENT_CREDENTIALS', inputFields });
          }}
        />
      </TemplateViewContainer>
    );

  if (current.matches('uiPollingSingleSelectImageTemplate'))
    return (
      <TemplateViewContainer onGoBack={handleGoBack}>
        <SelectableImageView
          fields={supplementalInformation}
          onSubmit={(inputFields) => {
            send({ type: 'EVENT_SUPPLEMENT_CREDENTIALS', inputFields });
          }}
        />
      </TemplateViewContainer>
    );

  if (current.matches('uiPollingInstructionTemplate'))
    return (
      <TemplateViewContainer onGoBack={handleGoBack}>
        <InstructionsSupplementalScreen
          title={legacyFields[0].value || ''}
          handleSubmit={(inputFields) => {
            send({ type: 'EVENT_SUPPLEMENT_CREDENTIALS', inputFields });
          }}
        />
      </TemplateViewContainer>
    );

  if (current.matches('uiPollingMultipleFieldsTemplate')) {
    return (
      <TemplateViewContainer onGoBack={handleGoBack}>
        <MultipleFieldsSupplementalScreen
          fields={legacyFields}
          onSubmit={(inputFields) => {
            send({ type: 'EVENT_SUPPLEMENT_CREDENTIALS', inputFields });
          }}
        />
      </TemplateViewContainer>
    );
  }

  if (current.matches('uiPollingImageQuestionTextAnswerTemplate')) {
    /**
     * Similar as `uiPollingMultipleFieldsTemplate` but component is called with full supplementalInformation
     * object which contains more data.
     */
    return (
      <TemplateViewContainer onGoBack={handleGoBack}>
        <ImageCodeView
          fields={supplementalInformation}
          onSubmit={(inputFields) => {
            send({ type: 'EVENT_SUPPLEMENT_CREDENTIALS', inputFields });
          }}
        />
      </TemplateViewContainer>
    );
  }

  if (current.matches('uiPollingCheckboxTemplate'))
    return (
      <TemplateViewContainer onGoBack={handleGoBack}>
        <CheckboxSupplementalScreen
          fields={legacyFields}
          onSubmit={(inputFields) => {
            send({ type: 'EVENT_SUPPLEMENT_CREDENTIALS', inputFields });
          }}
        />
      </TemplateViewContainer>
    );

  if (current.matches('uiPollingOtpMethodsTemplate'))
    return (
      <TemplateViewContainer onGoBack={handleGoBack}>
        <OtpMethodsTemplateScreen
          fields={supplementalInformation}
          handleSubmit={(inputFields) => {
            send({ type: 'EVENT_SUPPLEMENT_CREDENTIALS', inputFields });
          }}
        />
      </TemplateViewContainer>
    );

  if (current.matches('uiPollingVerificationTemplate'))
    return (
      <TemplateViewContainer onGoBack={handleGoBack}>
        <VerificationTemplateScreen
          fields={supplementalInformation}
          onSubmit={(inputFields) => {
            send({ type: 'EVENT_SUPPLEMENT_CREDENTIALS', inputFields });
          }}
        />
      </TemplateViewContainer>
    );

  if (current.matches('uiErrorUnknownTemplate'))
    return (
      <TemplateViewContainer onGoBack={handleGoBack}>
        <UnknownTemplateScreen
          templateType={getTemplateField(current.context.supplementalInformation)?.value}
        />
      </TemplateViewContainer>
    );

  return null;
};
